/**
 * Replacement plan editor — one old-device-to-new-item line per row, with
 * every quantity and cost computed server-side and re-rendered from the
 * response.
 *
 * Functional spec: `docs/features/REPLACEMENT-PLANNING.md` (the UI section
 * and the worked example). No Qt screen behind this one — web-only feature.
 *
 * **The plan stores inputs only.** `required`, `sets_on_hand`, `available`,
 * `take`, `to_order` and every cost are derived by `utils/replacement_engine.py`
 * at read time, so this screen never recomputes them locally — every edit is
 * sent to the server (debounced) and the grid is redrawn from whatever PUT /
 * import-commit / status-change last answered. Between keystrokes the
 * computed columns can lag by one debounce interval; that is the accepted
 * trade for "the server is the only place the arithmetic lives."
 *
 * **Autosave is optimistic + debounced**, unlike `BomDetail.tsx` (which saves
 * on every keystroke with no debounce — acceptable there because a BOM line
 * has one editable number). This screen has many editable fields across a
 * wider grid, so every edit first writes straight into the React Query cache
 * (so the input feels instant) and only *schedules* the network PUT; a
 * further edit within the debounce window replaces the pending one rather
 * than queuing both.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  ActionIcon, Alert, Box, Button, Card, Group, List, Loader, NumberInput,
  Paper, Select, SimpleGrid, Stack, Table, Text, TextInput, Title, Tooltip,
} from '@mantine/core'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { useNavigate, useParams } from 'react-router-dom'

import { ApiError, api } from '../api/client'
import type {
  BOM, Customer, CustomerBudget, InventorySnapshot, Issuance, ItemRow,
  MasterData, PlanComputed, PlanLineComputed, PlanStatus,
  ReplacementPlanInput,
} from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { confirmAction, confirmDelete } from '../components/confirm'
import { STICKY_END, STICKY_START } from '../components/DataGrid'
import { HiddenAmount } from '../components/HiddenAmount'
import { OldDeviceField } from '../components/OldDeviceField'
import { ReplacementLineWizard, type GuidedLineInput } from '../components/ReplacementLineWizard'
import { StatusBadge } from '../components/StatusBadge'
import { fmtMoney, fmtQty } from '../format'
import { ItemPicker } from './ItemPicker'
import { ReplacementImportModal } from './ReplacementImportModal'
import { PLAN_STATUS_LABEL_HE } from './ReplacementPlans'

const NO_SNAPSHOT = 'none'
/** How long to wait after the last keystroke before persisting. Short enough
 * that "נשמר ✓" still reads as prompt, long enough that typing a name or a
 * quantity does not fire a PUT per character. */
const SAVE_DEBOUNCE_MS = 700

function money(value: number | null, currency: string) {
  return value == null ? <HiddenAmount /> : <>{fmtMoney(value, currency)}</>
}

/** `PlanComputed` (what the server returns) -> `ReplacementPlanInput` (what a
 * PUT sends back) — drops every field the engine derives (item_name,
 * required, take, hardware, warnings, …) and the line `id` (the DAO replaces
 * the whole line set on every save; a client never round-trips one). */
function toInput(computed: PlanComputed): ReplacementPlanInput {
  return {
    name: computed.plan.name,
    snapshot_id: computed.plan.snapshot_id,
    dealer_id: computed.plan.dealer_id,
    customer_id: computed.plan.customer_id,
    budget_id: computed.plan.budget_id,
    currency: computed.plan.currency,
    vat_pct: computed.plan.vat_pct,
    notes: computed.plan.notes,
    lines: computed.lines.map((l) => ({
      old_label: l.old_label,
      old_sku: l.old_sku,
      old_qty: l.old_qty,
      new_item_id: l.new_item_id,
      ratio: l.ratio,
      reserve_override: l.reserve_override,
      take_override: l.take_override,
      install_cost_per_unit: l.install_cost_per_unit,
      term_months: l.term_months,
      notes: l.notes,
      sort_order: l.sort_order,
    })),
  }
}

function blankLine(item: ItemRow, sortOrder: number): PlanLineComputed {
  return {
    id: null, old_label: '', old_sku: null, old_qty: 0,
    new_item_id: item.id!, ratio: 1, reserve_override: null,
    take_override: null, install_cost_per_unit: 0, term_months: null, notes: '',
    sort_order: sortOrder, item_name: item.name,
    item_manufacturer: item.manufacturer, terms_available: item.terms_available,
    required: 0, sets_on_hand: 0,
    reserve: 0, allocated_elsewhere: 0, available: 0, take: 0, capped: false,
    to_order: 0, hardware: 0, install: 0,
    // A termed item's freshly-blanked line starts blocked, same as a brand
    // new BOM line — the server will confirm this on the very next save, but
    // rendering it blocked immediately (rather than waiting a round trip)
    // means the wizard's own "pick a term" step is never shown as optional
    // when it is not.
    blocked: item.is_termed, block_reason: item.is_termed ? 'טרם נבחרה תקופה' : '',
    warnings: [],
  }
}

export function ReplacementPlanEditorPage() {
  const { id } = useParams()
  const planId = Number(id)
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { canEdit, isAdmin } = useAuth()
  const queryKey = useMemo(() => ['replacement-plan', planId] as const, [planId])

  // The new-item picker only ever changes an *existing* line's item now —
  // adding a line is the guided wizard below, which owns its own new-item
  // ItemPicker instance.
  const [settingItemIndex, setSettingItemIndex] = useState<number | null>(null)
  const [importing, setImporting] = useState(false)
  const [wizardOpen, setWizardOpen] = useState(false)
  const [dirty, setDirty] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // A line just added through the guided wizard, keyed by its `sort_order`
  // (stable across the save round-trip — ReplacementPlanDAO.save() stores it
  // exactly as given, never renumbered) rather than by array index or `id`:
  // an unsaved line has no `id` yet, and its index would shift if another
  // line were removed while the highlight is still fading. Drives both the
  // scroll-into-view and the brief highlight below.
  const [highlightSortOrder, setHighlightSortOrder] = useState<number | null>(null)
  const rowRefs = useRef<Map<number, HTMLTableRowElement>>(new Map())

  const query = useQuery({
    queryKey,
    queryFn: () => api.get<PlanComputed>(`/replacement-plans/${planId}`),
    enabled: Number.isFinite(planId),
  })
  const plan = query.data

  const snapshots = useQuery({
    queryKey: ['snapshots'],
    queryFn: () => api.get<InventorySnapshot[]>('/inventory/snapshots'),
  })
  const master = useQuery({
    queryKey: ['master-data'],
    queryFn: () => api.get<MasterData>('/master-data'),
  })
  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: () => api.get<Customer[]>('/customers'),
  })
  const customerId = plan?.plan.customer_id ?? null
  const budgets = useQuery({
    queryKey: ['budgets', customerId],
    queryFn: () => api.get<CustomerBudget[]>(`/customers/${customerId}/budgets`),
    enabled: customerId != null,
  })

  // The plan's own selected snapshot's lines, if any — the old-device cell's
  // on-hand hint reads this (`onHandForSku`), same source the engine itself
  // reads via `InventorySnapshot.qty_for_sku()` for every other on-hand
  // figure on this screen, so the hint never implies stock the engine would
  // not also see.
  const selectedSnapshotLines = useMemo(
    () => snapshots.data?.find((s) => s.id === plan?.plan.snapshot_id)?.lines,
    [snapshots.data, plan?.plan.snapshot_id],
  )

  // Clear the pending timer on unmount so a save scheduled just before
  // navigating away never fires against an unmounted screen's stale closure.
  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
  }, [])

  // Scroll the guided wizard's freshly-added line into view and give it a
  // brief highlight once it is actually on screen — covers both the
  // optimistic render (immediately) and the real one the save response
  // replaces it with a moment later (the effect re-runs on every `lines`
  // change while a highlight is pending, so it re-centers then too).
  useEffect(() => {
    if (highlightSortOrder == null) return
    const row = rowRefs.current.get(highlightSortOrder)
    row?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
    const timer = setTimeout(() => setHighlightSortOrder(null), 1800)
    return () => clearTimeout(timer)
  }, [highlightSortOrder, plan?.lines])

  const saveMutation = useMutation({
    mutationFn: (payload: ReplacementPlanInput) =>
      api.put<PlanComputed>(`/replacement-plans/${planId}`, payload),
    onSuccess: (data) => {
      qc.setQueryData(queryKey, data)
      setDirty(false)
    },
    onError: (err) => {
      notifications.show({
        color: 'red',
        message: err instanceof ApiError ? err.message : 'השמירה נכשלה',
      })
      // The optimistic write above may no longer match the server. Refetch
      // so the screen does not keep showing an edit that never actually
      // persisted (e.g. a 409 because the plan was converted elsewhere).
      void qc.invalidateQueries({ queryKey })
      setDirty(false)
    },
  })

  /** Write `next` into the cache immediately (so every input renders the
   * value just typed with no round trip) and schedule the actual PUT,
   * replacing any still-pending one — unless `immediate`, which fires the PUT
   * right away instead of waiting out the debounce. Used for the guided
   * wizard's one-shot add: that is a single deliberate action, not a
   * keystroke in a longer edit, so there is no "next edit" to coalesce with,
   * and the whole point of the guided flow is seeing the real computed row
   * appear as soon as the save response allows rather than after an extra,
   * pointless wait. */
  function commit(next: PlanComputed, opts?: { immediate?: boolean }) {
    qc.setQueryData(queryKey, next)
    setDirty(true)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (opts?.immediate) {
      saveMutation.mutate(toInput(next))
    } else {
      debounceRef.current = setTimeout(() => {
        saveMutation.mutate(toInput(next))
      }, SAVE_DEBOUNCE_MS)
    }
  }

  function updateHeader(patch: Partial<PlanComputed['plan']>) {
    if (!plan) return
    commit({ ...plan, plan: { ...plan.plan, ...patch } })
  }

  function updateLine(index: number, patch: Partial<PlanLineComputed>) {
    if (!plan) return
    commit({ ...plan, lines: plan.lines.map((l, i) => (i === index ? { ...l, ...patch } : l)) })
  }

  function removeLine(index: number) {
    if (!plan) return
    commit({ ...plan, lines: plan.lines.filter((_, i) => i !== index) })
  }

  /** The guided wizard's finished answer -> a new line, built on `blankLine`'s
   * same zero-defaulted shape (the server recomputes the real
   * `required`/`take`/`to_order`/cost figures on the very next render, same
   * as any other edit on this screen) with the four guided fields overlaid.
   * Saved immediately (not debounced) so those real figures come back as
   * fast as the network allows, then scrolled into view and briefly
   * highlighted once rendered (see the `highlightSortOrder` effect above). */
  function addGuidedLine(input: GuidedLineInput) {
    if (!plan) return
    const nextSort = plan.lines.length
      ? Math.max(...plan.lines.map((l) => l.sort_order)) + 1 : 0
    const line: PlanLineComputed = {
      ...blankLine(input.item, nextSort),
      old_label: input.old_label, old_sku: input.old_sku,
      old_qty: input.old_qty, ratio: input.ratio,
      term_months: input.term_months ?? null,
      // Best-effort optimistic render — the immediate save a few lines down
      // confirms (or corrects) this against the server within one round
      // trip either way, same as every other derived field here.
      ...(input.term_months != null ? { blocked: false, block_reason: '' } : {}),
    }
    commit({ ...plan, lines: [...plan.lines, line] }, { immediate: true })
    setHighlightSortOrder(nextSort)
  }

  const statusMutation = useMutation({
    mutationFn: (status: PlanStatus) =>
      api.post<PlanComputed>(`/replacement-plans/${planId}/status`, { status }),
    onSuccess: (data) => {
      qc.setQueryData(queryKey, data)
      void qc.invalidateQueries({ queryKey: ['replacement-plans'] })
      notifications.show({ message: 'סטטוס התוכנית עודכן', color: 'green' })
    },
    onError: (err) =>
      notifications.show({
        color: 'red',
        message: err instanceof ApiError ? err.message : 'עדכון הסטטוס נכשל',
      }),
  })

  const convertToBom = useMutation({
    mutationFn: () => api.post<BOM>(`/replacement-plans/${planId}/convert-to-bom`),
    onSuccess: (bom) => {
      void qc.invalidateQueries({ queryKey: ['replacement-plans'] })
      void qc.invalidateQueries({ queryKey: ['boms'] })
      notifications.show({ message: `נוצר BOM "${bom.project_name}"`, color: 'green' })
      navigate(`/bom/${bom.id}`)
    },
    onError: (err) =>
      notifications.show({
        color: 'red',
        message: err instanceof ApiError ? err.message : 'ההמרה ל-BOM נכשלה',
      }),
  })

  const convertToIssuance = useMutation({
    mutationFn: () => api.post<Issuance>(`/replacement-plans/${planId}/convert-to-issuance`),
    onSuccess: (issuance) => {
      // Refetch rather than hand-patch the cache: the server already wrote
      // issuance_id before answering, so the next GET carries it — same
      // "write response equals the next GET" contract every other write on
      // this plan relies on.
      void qc.invalidateQueries({ queryKey })
      void qc.invalidateQueries({ queryKey: ['issuances'] })
      notifications.show({
        message: `נוצר ניפוק ${issuance.issuance_number}`, color: 'green',
      })
    },
    onError: (err) =>
      notifications.show({
        color: 'red',
        message: err instanceof ApiError ? err.message : 'ההמרה לניפוק נכשלה',
      }),
  })

  const deleteMutation = useMutation({
    mutationFn: () => api.delete<unknown>(`/replacement-plans/${planId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['replacement-plans'] })
      notifications.show({ message: 'התוכנית נמחקה', color: 'green' })
      navigate('/inventory')
    },
    onError: (err) =>
      notifications.show({
        color: 'red',
        message: err instanceof ApiError ? err.message : 'מחיקת התוכנית נכשלה',
      }),
  })

  if (query.isLoading) return <Text>טוען…</Text>
  if (!plan) return <Alert color="red">תוכנית ההחלפה לא נמצאה.</Alert>

  const status = plan.plan.status as PlanStatus
  // Mirrors ReplacementPlan.is_locked() in database/models.py exactly: these
  // two statuses are terminal, and ReplacementPlanDAO.save() refuses to edit
  // a plan the row already shows as either — approval alone does NOT lock
  // editing, since DRAFT <-> APPROVED goes both ways.
  const locked = status === 'CONVERTED' || status === 'CANCELLED'
  const readOnly = !canEdit || locked

  async function cancelPlan() {
    if (!(await confirmAction({
      title: 'ביטול תוכנית',
      message: 'לבטל את תוכנית ההחלפה? ביטול הוא סופי — לא ניתן להחזיר תוכנית '
        + 'מבוטלת לטיוטה.',
      confirmLabel: 'בטל תוכנית',
    }))) return
    statusMutation.mutate('CANCELLED')
  }

  async function deletePlan() {
    // Narrowed already by the `if (!plan) return` above — but this is a
    // hoisted function declaration, and TS does not carry a const narrowing
    // across one of those (unlike the arrow-function helpers above it), so
    // the guard is repeated here purely to satisfy the type checker.
    if (!plan) return
    if (!(await confirmDelete(`תוכנית ההחלפה "${plan.plan.name}"`))) return
    deleteMutation.mutate()
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <Group gap="sm" wrap="nowrap">
          <Button variant="subtle" size="compact-sm" onClick={() => navigate('/inventory')}>
            ← חזרה
          </Button>
          <TextInput
            value={plan.plan.name}
            disabled={readOnly}
            onChange={(e) => updateHeader({ name: e.currentTarget.value })}
            aria-label="שם התוכנית"
            w={260}
          />
          <StatusBadge value={status} label={PLAN_STATUS_LABEL_HE[status]} />
        </Group>
        <Group gap="xs">
          {dirty || saveMutation.isPending ? (
            <Group gap={6}>
              <Loader size="xs" />
              <Text size="xs" c="dimmed">שומר…</Text>
            </Group>
          ) : saveMutation.isSuccess ? (
            <Text size="xs" c="teal">נשמר ✓</Text>
          ) : null}
        </Group>
      </Group>

      {locked && (
        <Alert color={status === 'CONVERTED' ? 'teal' : 'gray'} variant="light">
          <Group justify="space-between" wrap="wrap">
            <Text size="sm">
              {status === 'CONVERTED'
                ? 'התוכנית הומרה להזמנה ואינה ניתנת לעריכה.'
                : 'התוכנית בוטלה ואינה ניתנת לעריכה.'}
            </Text>
            {status === 'CONVERTED' && plan.plan.bom_id != null && (
              <Button size="xs" variant="light" onClick={() => navigate(`/bom/${plan.plan.bom_id}`)}>
                פתח את ה-BOM
              </Button>
            )}
          </Group>
        </Alert>
      )}

      <Paper withBorder p="sm">
        <Title order={5} mb="sm">פרטי התוכנית</Title>
        <Stack gap="sm">
          <Group grow align="flex-start">
            <Select
              label="צילום מלאי"
              disabled={readOnly}
              data={[
                { value: NO_SNAPSHOT, label: 'ללא מלאי' },
                ...(snapshots.data ?? []).map((s) => ({
                  value: String(s.id), label: s.snapshot_name,
                })),
              ]}
              value={plan.plan.snapshot_id != null ? String(plan.plan.snapshot_id) : NO_SNAPSHOT}
              onChange={(v) => updateHeader({
                snapshot_id: v && v !== NO_SNAPSHOT ? Number(v) : null,
              })}
            />
            <Select
              label="דילר"
              searchable
              clearable
              clearButtonProps={{ 'aria-label': 'נקה', size: 'md' }}
              disabled={readOnly}
              data={(master.data?.dealers ?? []).map((d) => ({
                value: String(d.id), label: d.name,
              }))}
              value={plan.plan.dealer_id != null ? String(plan.plan.dealer_id) : null}
              onChange={(v) => updateHeader({ dealer_id: v ? Number(v) : null })}
            />
          </Group>
          <Group grow align="flex-start">
            <Select
              label="לקוח"
              description="עם תקציב מפעיל את המרת ההחלפה מהמחסן לניפוק"
              searchable
              clearable
              clearButtonProps={{ 'aria-label': 'נקה', size: 'md' }}
              disabled={readOnly}
              data={(customers.data ?? []).map((c) => ({
                value: String(c.id), label: c.name,
              }))}
              value={plan.plan.customer_id != null ? String(plan.plan.customer_id) : null}
              onChange={(v) => updateHeader({
                customer_id: v ? Number(v) : null,
                // A budget belongs to one customer; changing the customer
                // without clearing this would leave a budget_id dangling
                // from whoever was selected before.
                budget_id: null,
              })}
            />
            <Select
              label="תקציב"
              disabled={readOnly || !plan.plan.customer_id}
              placeholder={plan.plan.customer_id ? undefined : 'בחר לקוח תחילה'}
              clearable
              clearButtonProps={{ 'aria-label': 'נקה', size: 'md' }}
              data={(budgets.data ?? []).map((b) => ({
                value: String(b.id), label: `${b.name} (${b.currency})`,
              }))}
              value={plan.plan.budget_id != null ? String(plan.plan.budget_id) : null}
              onChange={(v) => updateHeader({ budget_id: v ? Number(v) : null })}
            />
          </Group>
          <Group grow align="flex-start">
            <Select
              label="מטבע"
              disabled={readOnly}
              data={['USD', 'ILS']}
              value={plan.plan.currency}
              onChange={(v) => updateHeader({ currency: v ?? 'USD' })}
            />
            <NumberInput
              label='מע"מ %'
              hideControls
              min={0}
              max={100}
              decimalScale={2}
              disabled={readOnly}
              value={plan.plan.vat_pct}
              onChange={(v) => updateHeader({ vat_pct: Number(v) || 0 })}
            />
          </Group>
        </Stack>
      </Paper>

      <Paper withBorder p="sm">
        <Group justify="space-between" mb="xs" wrap="wrap">
          <Title order={5}>שורות התוכנית</Title>
          {!readOnly && (
            <Group gap="xs">
              <Button size="xs" variant="light" onClick={() => setWizardOpen(true)}>
                הוסף שורה
              </Button>
              <Button size="xs" variant="light" onClick={() => setImporting(true)}>
                ייבוא CSV
              </Button>
            </Group>
          )}
        </Group>

        {plan.lines.length === 0 ? (
          <Text c="dimmed" size="sm">
            אין שורות בתוכנית. השתמש ב"הוסף שורה" או ב"ייבוא CSV".
          </Text>
        ) : (
          <Box style={{ overflowX: 'auto' }}>
            {/* 13 columns of inputs and money — without a minimum width the
                table shrinks to fit a narrow viewport instead of scrolling,
                which is exactly how a cell drops below the 24px WCAG target
                (measured: uxaudit.mjs found a 23px-wide input at phone width
                before this). DataGrid.tsx hits the same number from the
                other direction (`Math.max(640, columns.length * 110)`) for
                the same reason.

                Sideways scroll is the deliberate choice here (same as
                `BomDetail`'s component grid) rather than `DataGrid`'s
                column-hiding — every one of these 13 columns is either an
                input or a number the engine computed from one, so there is no
                "low priority" column to drop. `STICKY_START`/`STICKY_END` are
                the same two constants `DataGrid` pins its own first/action
                column with, kept in sync with it rather than copied, since
                this table cannot use `DataGrid` itself (its rows are a form,
                not data — every cell but the computed ones is a live input). */}
            <Table withColumnBorders miw={1400}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ ...STICKY_START, width: 200, zIndex: 3 }}>ציוד ישן</Table.Th>
                  <Table.Th style={{ width: 90 }}>כמות</Table.Th>
                  <Table.Th style={{ width: 80 }}>יחס</Table.Th>
                  <Table.Th style={{ width: 180 }}>פריט חדש</Table.Th>
                  <Table.Th style={{ width: 130 }}>תקופה</Table.Th>
                  <Table.Th style={{ width: 70 }}>נדרש</Table.Th>
                  <Table.Th style={{ width: 70 }}>במחסן</Table.Th>
                  <Table.Th style={{ width: 100 }}>רזרבה</Table.Th>
                  <Table.Th style={{ width: 140 }}>לקיחה מהמחסן</Table.Th>
                  <Table.Th style={{ width: 80 }}>להזמנה</Table.Th>
                  <Table.Th style={{ width: 110 }}>חומרה</Table.Th>
                  <Table.Th style={{ width: 110 }}>התקנה ליח׳</Table.Th>
                  <Table.Th style={{ width: 110 }}>התקנה</Table.Th>
                  <Table.Th style={{ ...STICKY_END, width: 40, zIndex: 3 }} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {plan.lines.map((line, index) => (
                  <Table.Tr
                    key={line.id ?? `new-${index}`}
                    ref={(el) => {
                      if (el) rowRefs.current.set(line.sort_order, el)
                      else rowRefs.current.delete(line.sort_order)
                    }}
                    style={highlightSortOrder === line.sort_order
                      ? { backgroundColor: 'var(--mantine-color-yellow-light)' }
                      : line.blocked
                        ? { backgroundColor: 'var(--mantine-color-red-0)' }
                        : undefined}
                  >
                    <Table.Td style={{ ...STICKY_START, zIndex: 1 }}>
                      <OldDeviceField
                        value={{ old_label: line.old_label, old_sku: line.old_sku }}
                        onChange={(patch) => updateLine(index, patch)}
                        disabled={readOnly}
                        snapshotLines={selectedSnapshotLines}
                      />
                    </Table.Td>
                    <Table.Td>
                      <NumberInput
                        size="xs" hideControls min={0} decimalScale={2} disabled={readOnly}
                        value={line.old_qty}
                        onChange={(v) => updateLine(index, { old_qty: Number(v) || 0 })}
                      />
                    </Table.Td>
                    <Table.Td>
                      <NumberInput
                        size="xs" hideControls min={0.01} decimalScale={2} disabled={readOnly}
                        value={line.ratio}
                        onChange={(v) => updateLine(index, { ratio: Number(v) || 1 })}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Button
                        size="xs" variant={readOnly ? 'subtle' : 'light'} fullWidth
                        disabled={readOnly}
                        onClick={() => setSettingItemIndex(index)}
                      >
                        {line.item_name || 'בחר פריט…'}
                      </Button>
                    </Table.Td>
                    <Table.Td>
                      {line.terms_available.length > 0 && (
                        <Group gap={4} wrap="nowrap">
                          <Select
                            size="xs"
                            placeholder="בחר תקופה"
                            aria-label="תקופה"
                            disabled={readOnly}
                            style={{ flex: 1 }}
                            data={line.terms_available.map((t) => ({
                              value: String(t), label: `${t} חודשים`,
                            }))}
                            value={line.term_months != null ? String(line.term_months) : null}
                            onChange={(v) => updateLine(index, { term_months: v ? Number(v) : null })}
                          />
                          {line.blocked && (
                            <Tooltip label={line.block_reason}>
                              <Text c="red.7" fw={700} style={{ cursor: 'help' }}>⚠</Text>
                            </Tooltip>
                          )}
                        </Group>
                      )}
                    </Table.Td>
                    <Table.Td><Text size="sm">{fmtQty(line.required)}</Text></Table.Td>
                    <Table.Td><Text size="sm">{fmtQty(line.sets_on_hand)}</Text></Table.Td>
                    <Table.Td>
                      <NumberInput
                        size="xs" hideControls min={0} decimalScale={2} disabled={readOnly}
                        placeholder={fmtQty(line.reserve)}
                        value={line.reserve_override ?? ''}
                        onChange={(v) => updateLine(index, {
                          reserve_override: v === '' ? null : Number(v),
                        })}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap">
                        <NumberInput
                          size="xs" hideControls min={0} decimalScale={2} disabled={readOnly}
                          placeholder={fmtQty(line.take)}
                          value={line.take_override ?? ''}
                          onChange={(v) => updateLine(index, {
                            take_override: v === '' ? null : Number(v),
                          })}
                          style={{ flex: 1 }}
                        />
                        {line.capped && (
                          <Tooltip label={line.warnings.join('\n') || 'הכמות הוגבלה למקסימום הזמין'}>
                            <Text c="yellow.7" fw={700} style={{ cursor: 'help' }}>⚠</Text>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td><Text size="sm">{fmtQty(line.to_order)}</Text></Table.Td>
                    <Table.Td><Text size="sm">{money(line.hardware, plan.plan.currency)}</Text></Table.Td>
                    <Table.Td>
                      <NumberInput
                        size="xs" hideControls min={0} decimalScale={2} disabled={readOnly}
                        value={line.install_cost_per_unit}
                        onChange={(v) => updateLine(index, { install_cost_per_unit: Number(v) || 0 })}
                      />
                    </Table.Td>
                    <Table.Td><Text size="sm">{money(line.install, plan.plan.currency)}</Text></Table.Td>
                    <Table.Td style={{ ...STICKY_END, zIndex: 2 }}>
                      {!readOnly && (
                        <ActionIcon
                          size={30} color="red" variant="subtle" aria-label="הסר שורה"
                          onClick={() => removeLine(index)}
                        >
                          ✕
                        </ActionIcon>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Box>
        )}

        {plan.warnings.length > 0 && (
          <Alert color="yellow" variant="light" mt="sm" title={`${plan.warnings.length} אזהרות`}>
            <List size="sm" spacing={2}>
              {plan.warnings.map((w, i) => <List.Item key={i}>{w}</List.Item>)}
            </List>
          </Alert>
        )}
      </Paper>

      <SimpleGrid cols={{ base: 1, sm: 3, lg: 5 }}>
        <Card withBorder padding="sm">
          <Text size="xs" c="dimmed">חומרה</Text>
          <Text fw={700} size="lg">{money(plan.hardware_subtotal, plan.plan.currency)}</Text>
        </Card>
        <Card withBorder padding="sm">
          <Text size="xs" c="dimmed">התקנה</Text>
          <Text fw={700} size="lg">{money(plan.install_subtotal, plan.plan.currency)}</Text>
        </Card>
        <Card withBorder padding="sm">
          <Text size="xs" c="dimmed">מע״מ</Text>
          <Text fw={700} size="lg">{money(plan.vat, plan.plan.currency)}</Text>
        </Card>
        <Card withBorder padding="sm">
          <Text size="xs" c="dimmed">סה״כ</Text>
          <Text fw={700} size="lg">{money(plan.total, plan.plan.currency)}</Text>
        </Card>
        <Card withBorder padding="sm">
          <Text size="xs" c="dimmed">מכשירים</Text>
          <Text size="sm">להחלפה {fmtQty(plan.devices_replaced)}</Text>
          <Text size="sm">מהמחסן {fmtQty(plan.devices_from_stock)}</Text>
          <Text size="sm">להזמנה {fmtQty(plan.devices_to_order)}</Text>
          {/* DP-11: lines priced at 0 for a missing/incomplete term choice —
              mirrors BomDetail.tsx's own blocked-lines banner. */}
          {plan.blocked_lines > 0 && (
            <Text size="sm" c="red">{plan.blocked_lines} שורות חסומות</Text>
          )}
        </Card>
      </SimpleGrid>

      <Group justify="space-between" wrap="wrap">
        <Group gap="xs" wrap="wrap">
          {!locked && canEdit && status === 'DRAFT' && (
            <Button
              variant="light"
              loading={statusMutation.isPending}
              onClick={() => statusMutation.mutate('APPROVED')}
            >
              אשר
            </Button>
          )}
          {!locked && canEdit && status === 'APPROVED' && (
            <>
              <Button
                variant="default"
                loading={statusMutation.isPending}
                onClick={() => statusMutation.mutate('DRAFT')}
              >
                החזר לטיוטה
              </Button>
              <Button
                loading={convertToBom.isPending}
                onClick={() => void confirmAction({
                  title: 'המרת תוכנית ל-BOM',
                  message: `להמיר את התוכנית ל-BOM? ה-BOM יזמין ${fmtQty(plan.devices_to_order)} `
                    + 'מכשירים — בדיוק מה שהתוכנית מסמנת "להזמנה", ללא ניכוי נוסף.',
                  confirmLabel: 'המר ל-BOM',
                  danger: false,
                }).then((ok) => ok && convertToBom.mutate())}
              >
                צור BOM להזמנה
              </Button>
            </>
          )}
          {!locked && canEdit && plan.plan.customer_id != null && plan.plan.budget_id != null && (
            <Button
              variant="light"
              loading={convertToIssuance.isPending}
              disabled={plan.plan.issuance_id != null}
              onClick={() => convertToIssuance.mutate()}
            >
              צור ניפוק מהמחסן
            </Button>
          )}
        </Group>
        <Group gap="xs">
          {!locked && canEdit && (status === 'DRAFT' || status === 'APPROVED') && (
            <Button
              variant="subtle" color="orange"
              loading={statusMutation.isPending}
              onClick={() => void cancelPlan()}
            >
              בטל תוכנית
            </Button>
          )}
          {isAdmin && (status === 'DRAFT' || status === 'CANCELLED') && (
            <Button
              variant="subtle" color="red"
              loading={deleteMutation.isPending}
              onClick={() => void deletePlan()}
            >
              מחק
            </Button>
          )}
        </Group>
      </Group>

      <ItemPicker
        opened={settingItemIndex != null}
        onClose={() => setSettingItemIndex(null)}
        onPick={(items) => {
          const item = items[0]
          if (item && settingItemIndex != null) {
            updateLine(settingItemIndex, {
              new_item_id: item.id!, item_name: item.name,
              item_manufacturer: item.manufacturer,
            })
          }
          setSettingItemIndex(null)
        }}
        title="בחירת פריט חדש"
        confirmLabel="בחר"
      />

      <ReplacementLineWizard
        opened={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onSubmit={addGuidedLine}
        snapshotLines={selectedSnapshotLines}
      />

      <ReplacementImportModal
        opened={importing}
        planId={planId}
        onClose={() => setImporting(false)}
        onCommitted={(computed) => {
          qc.setQueryData(queryKey, computed)
          void qc.invalidateQueries({ queryKey: ['replacement-plans'] })
        }}
      />
    </Stack>
  )
}
